import type { LiveChatMode } from '@/features/live/ablySessionChannels';

const CHAT_MODES: readonly LiveChatMode[] = ['PUBLIC', 'PRIVATE', 'DISABLED'];

function isLiveChatMode(value: unknown): value is LiveChatMode {
  return CHAT_MODES.includes(value as LiveChatMode);
}

/**
 * Un Evergreen issu d'un live rejoue les changements de mode du chat au timecode où ils ont eu lieu :
 * le back publie alors cet événement sur le canal de la session.
 */
export function readChatModeChange(
  messageName: string | undefined,
  data: unknown,
): LiveChatMode | null {
  if (messageName !== 'event-message' || !data || typeof data !== 'object') {
    return null;
  }
  const { type, chatMode } = data as { type?: unknown; chatMode?: unknown };
  if (type !== 'CHAT_MODE_CHANGED' || !isLiveChatMode(chatMode)) {
    return null;
  }
  return chatMode;
}

export type ReplayedChatMode = { sessionIdentifier: string; chatMode: LiveChatMode };

/**
 * Le mode rejoué arrive par deux voies sans ordre garanti : l'événement Ably et la configuration
 * relue en HTTP. Une réponse ne s'applique que si c'est la dernière requête lancée et qu'aucun
 * événement n'est arrivé depuis son départ, sinon elle pourrait être plus ancienne que lui.
 */
export type EvergreenChatModeSync = {
  replayed: ReplayedChatMode | null;
  eventCount: number;
  latestRequest: { requestId: string; eventCountAtStart: number } | null;
};

export const initialEvergreenChatModeSync: EvergreenChatModeSync = {
  replayed: null,
  eventCount: 0,
  latestRequest: null,
};

export function chatModeEventReceived(
  sync: EvergreenChatModeSync,
  replayed: ReplayedChatMode,
): EvergreenChatModeSync {
  return { ...sync, replayed, eventCount: sync.eventCount + 1 };
}

export function chatModeRequestStarted(
  sync: EvergreenChatModeSync,
  requestId: string,
): EvergreenChatModeSync {
  return { ...sync, latestRequest: { requestId, eventCountAtStart: sync.eventCount } };
}

export function chatModeRequestFulfilled(
  sync: EvergreenChatModeSync,
  requestId: string,
  sessionIdentifier: string,
  chatMode: LiveChatMode | null | undefined,
): EvergreenChatModeSync {
  const { latestRequest } = sync;
  if (
    !chatMode ||
    latestRequest?.requestId !== requestId ||
    latestRequest.eventCountAtStart !== sync.eventCount
  ) {
    return sync;
  }
  return { ...sync, replayed: { sessionIdentifier, chatMode } };
}

export function replayedChatModeFor(
  replayed: ReplayedChatMode | null,
  sessionIdentifier: string | null | undefined,
): LiveChatMode | null {
  return replayed && replayed.sessionIdentifier === sessionIdentifier ? replayed.chatMode : null;
}

type RegieChatSettings = { chatMode: LiveChatMode };

/**
 * En Evergreen, la régie affiche le mode rejoué, celui que le serveur applique aux participants,
 * et n'écrit jamais le réglage du webinaire, qui sert de mode de départ aux sessions suivantes.
 */
export function regieDisplayedSettings<T extends RegieChatSettings>(
  settings: T,
  isEvergreen: boolean,
  replayedChatMode: LiveChatMode | null,
): T {
  return isEvergreen && replayedChatMode ? { ...settings, chatMode: replayedChatMode } : settings;
}

/**
 * En Evergreen, un changement de mode passe par la commande de session : il ne vaut que pour la
 * session en cours et prend le pas sur les changements rejoués.
 */
export function evergreenChatModeCommand<T extends RegieChatSettings>(
  next: T,
  displayed: T,
  isEvergreen: boolean,
): LiveChatMode | null {
  return isEvergreen && next.chatMode !== displayed.chatMode ? next.chatMode : null;
}

export function regieSettingsToSave<T extends RegieChatSettings>(
  next: T,
  isEvergreen: boolean,
  webinarChatMode: LiveChatMode,
): T {
  return isEvergreen ? { ...next, chatMode: webinarChatMode } : next;
}

type AttendeeChatModeContext = {
  isEvergreen: boolean;
  /** Mode rejoué de la session Evergreen affichée, absent tant que rien ne l'a fourni. */
  evergreenAttendeeChatMode?: LiveChatMode | null;
  /** Réglage poussé en direct par la régie. */
  liveOverrideChatMode?: LiveChatMode | null;
  sessionChatMode?: LiveChatMode | null;
};

export function resolveAttendeeChatMode({
  isEvergreen,
  evergreenAttendeeChatMode,
  liveOverrideChatMode,
  sessionChatMode,
}: AttendeeChatModeContext): LiveChatMode {
  if (isEvergreen && evergreenAttendeeChatMode) {
    return evergreenAttendeeChatMode;
  }
  return liveOverrideChatMode ?? sessionChatMode ?? 'PUBLIC';
}
